import { MatchHighlightsLink } from "@/features/highlights";

import { trackEvent } from "@/shared/analytics";

import { useMarkAnalysisViewed } from "../../api/useMarkAnalysisViewed";
import type { Analysis, MatchSummary } from "../../model/Analysis";
import type { AnalysisSection } from "../../model/AnalysisSection";
import { defaultSection } from "../../model/defaultSection";
import type { Report } from "../../model/Report";
import { selectFixFirst } from "../../model/selectFixFirst";
import { selectWentWell } from "../../model/selectWentWell";
import { selectWentWellFindings } from "../../model/selectWentWellFindings";
import { AnalysisSections } from "../AnalysisSections";
import { MainLesson } from "../MainLesson";
import { MatchMap } from "../MatchMap";
import { MoreFixes } from "../MoreFixes";
import { WentWell } from "../WentWell";

type AnalysisReportProps = {
  match: MatchSummary;
  analysis: Analysis;
  // Открытая секция из адреса; не задана — самая проблемная категория.
  section: AnalysisSection | undefined;
  onSectionChange: (section: AnalysisSection) => void;
};

// Готовый разбор по макету «Разбор матча к релизу»: сначала «Лучшее в матче» (хайлайты,
// HighlightsBest.dc.html), потом главное в матче крупно, ещё до двух
// ошибок и «получилось», секции категорий и тепловая карта матча. Объяснений LLM в MVP нет —
// экран полноценен без них. Показ отмечается для дашборда команды (`viewed`), открытие
// секции — действием `analysis_section` в Umami.
export const AnalysisReport = ({
  match,
  analysis,
  section,
  onSectionChange,
}: AnalysisReportProps) => {
  useMarkAnalysisViewed(analysis.id);
  const report: Report = { match, findings: analysis.findings, details: analysis.details };
  const open = section ?? defaultSection(report.findings);
  const fixes = selectFixFirst(report.findings);
  return (
    <div data-analysis-state="done" className="flex flex-col gap-5">
      <MatchHighlightsLink analysisId={analysis.id} />
      <MainLesson finding={fixes[0]} />
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(340px,100%),1fr))] gap-3 empty:hidden">
        <MoreFixes findings={fixes.slice(1)} />
        <WentWell
          findings={selectWentWellFindings(report.findings)}
          purchases={selectWentWell(report.details)}
        />
      </div>
      <AnalysisSections
        report={report}
        featured={fixes}
        open={open}
        onOpenChange={(next) => {
          if (next !== "none") {
            trackEvent("analysis_section");
          }
          onSectionChange(next);
        }}
      />
      <MatchMap analysisId={analysis.id} />
    </div>
  );
};
